import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useToast } from '@/components/ui/use-toast';
import { Batch } from '@/types';
import { batchApi } from '@/lib/api';
import { useBatch } from '@/contexts/BatchContext';

interface BatchFormProps {
  batch?: Batch;
  isEditing?: boolean;
  onSuccess?: () => void; // Callback for successful form submission
  showCancelButton?: boolean; // Whether to show cancel button
}

const BatchForm = ({ batch, isEditing = false, onSuccess, showCancelButton = true }: BatchFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { refetchBatches, batches } = useBatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<Batch>({
    defaultValues: batch || {
      year: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    }
  });

  useEffect(() => {
    if (batch) {
      // Set form values from batch data
      setValue('year', batch.year);
      setValue('startDate', batch.startDate);
      setValue('endDate', batch.endDate);
    }
  }, [batch, setValue]);

  const onSubmit = async (data: Batch) => {
    try {
      setIsSubmitting(true);

      // Validate that end date is after start date
      if (new Date(data.endDate) <= new Date(data.startDate)) {
        toast({
          title: 'Validation Error',
          description: 'End date must be after start date',
          variant: 'destructive',
        });
        return;
      }

      // Format the data to match the backend model
      const formattedData = {
        ...data,
        displayName: `Batch ${data.year}`, // Add required displayName field
      };

      console.log('Submitting batch data:', formattedData);

      if (isEditing && batch?.id) {
        // Update existing batch
        await batchApi.update(batch.id, formattedData);
        toast({
          title: 'Success',
          description: 'Batch updated successfully',
        });
      } else {
        // Create new batch
        await batchApi.create(formattedData);
        toast({
          title: 'Success',
          description: 'Batch created successfully',
        });
      }

      // Refresh batches in context
      await refetchBatches();

      // Call success callback if provided, otherwise navigate
      if (onSuccess) {
        onSuccess();
      } else {
        // Navigate back - if we came from BatchGuard (no batches), go to dashboard
        // Otherwise go to batches list
        const hasNoBatches = !batches || batches.length === 0;
        if (hasNoBatches) {
          navigate('/');
        } else {
          navigate('/settings/batches');
        }
      }
    } catch (error) {
      console.error('Error saving batch:', error);
      let errorMessage = 'Failed to save batch. Please try again.';

      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="year">Batch</Label>
          <Input
            id="year"
            placeholder="e.g., 2024-2025"
            {...register('year', { required: 'Batch is required' })}
          />
          {errors.year && (
            <p className="text-sm text-destructive">{errors.year.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="startDate">Start Date</Label>
          <Input
            id="startDate"
            type="date"
            {...register('startDate', { required: 'Start date is required' })}
          />
          {errors.startDate && (
            <p className="text-sm text-destructive">{errors.startDate.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="endDate">End Date</Label>
          <Input
            id="endDate"
            type="date"
            {...register('endDate', { required: 'End date is required' })}
          />
          {errors.endDate && (
            <p className="text-sm text-destructive">{errors.endDate.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        {showCancelButton && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (onSuccess) {
                // If we have a success callback, we're in a modal - don't navigate
                return;
              }
              const hasNoBatches = !batches || batches.length === 0;
              if (hasNoBatches) {
                navigate('/');
              } else {
                navigate('/settings/batches');
              }
            }}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (isEditing ? 'Updating batch...' : 'Creating batch...') : isEditing ? 'Update Batch' : 'Create Batch'}
        </Button>
      </div>
    </form>
  );
};

export default BatchForm;
